import { LitElement, html } from 'lit';
import fsTypography from '@cof/fs-typography-core/lib/typography.lit.min.js';
import { classMap } from 'lit-html/directives/class-map.js';
import { TagKey } from '@cof/tag-catalog-fs-vehicle-and-loan-mfe';
import { DebugService } from '../../shared/services/debug.service.js';
import { style } from './iconic-exit.style.js';
import '../iconic-container/iconic-container.component.js';
import '../iconic-vehicle-loan/iconic-vehicle-loan-card/iconic-vehicle-loan-card.component.js';
import '../iconic-vehicle/iconic-vehicle-card/iconic-vehicle-card.component.js';
import '../iconic-loan/iconic-loan-card/iconic-loan-card.component.js';
import { VehicleAndLoanService } from '../../services/vehicle-and-loan.service.js';
import { VehicleService } from '../../services/vehicle.service.js';
import { LoanService } from '../../services/loan.service.js';
import { stratum } from '../../../constants/stratum.js';

export class RefiIconicExit extends LitElement {
  constructor() {
    super();
    this.debug = new DebugService('RefiIconicExit');
    this.vehicles = [];
    this.vehicle = null;
    this.isVehicleIneligible = false;
    this.loans = [];
    this.loan = null;
    this.isLoanIneligible = false;
    this.matches = [];
    this.multiple = false;
    this.fromVehicleSearch = false;
  }

  static properties = {
    vehicle: { type: Object },
    loan: { type: Object },
    fromVehicleSearch: {
      type: Boolean,
      attribute: 'from-vehicle-search',
      reflect: true,
    },
  };

  static styles = [style, fsTypography];

  firstUpdated() {
    this.checkIneligibility();
    const ineligibleUnmatchedLoan =
      this.matches.length === 0 && this.isLoanIneligible;
    stratum.publishTag(
      TagKey.PAGE_VIEW_COAF_REFI_PREQUALIFY_VEHICLE_LOAN_EXIT,
      {
        replacements: {
          value: `${
            this.isVehicleIneligible
              ? 'ineligible vehicle'
              : ineligibleUnmatchedLoan
              ? 'ineligible loan no match'
              : 'ineligible loan'
          }`,
        },
      }
    );
  }

  connectedCallback() {
    super.connectedCallback();
    this.checkIneligibility();
    this.sendHostAppHeaderUpdates();
    this.dispatchEvent(
      new CustomEvent('show-progress-bar', {
        detail: {
          displayBar: false,
        },
        composed: true,
        bubbles: true,
      })
    );
  }

  render() {
    this.debug.log('rendered');
    return html`
      <refi-track-pageview pagename="exit"></refi-track-pageview>
      <div class="spacer"></div>
      <refi-iconic-container
        .headerText=${this.header()}
        ctaButtonText="Return to Capital One"
        @cta-clicked="${this.exitPrequal}"
        .returnButtonText=${this.returnText()}
        @return-cta-clicked="${this.refinanceDifferentCarLoan}"
      >
        <div class="subheader">${this.subheader()}</div>
        <div class="card ${classMap({ 'card-multiple': this.multiple })}">
          ${this.card()}
        </div>
      </refi-iconic-container>
    `;
  }

  checkIneligibility() {
    if (this.fromVehicleSearch) {
      if (VehicleService.vehicleList.length) {
        const unmatchedVehicles = VehicleService.vehicleList;
        // eslint-disable-next-line prefer-destructuring
        this.vehicle = unmatchedVehicles[0];
        this.isVehicleIneligible = !VehicleAndLoanService.isVehicleEligible(
          this.vehicle
        );
        this.vehicles = [this.vehicle];
        this.multiple = unmatchedVehicles.length > 1;
      }
    } else {
      const { vehicleAndLoanMatchList, vehicleList, loanList } =
        VehicleAndLoanService;
      this.matches = vehicleAndLoanMatchList;
      this.vehicles = vehicleList;
      this.loans = loanList;
      // evaluates matches if they exist
      if (this.matches.length > 0) {
        // checks eligibility of first match in list
        this.vehicle = this.matches[0].vehicle;
        this.isVehicleIneligible = !VehicleAndLoanService.isVehicleEligible(
          this.vehicle
        );
        this.loan = this.matches[0].loan;
        this.isLoanIneligible = !VehicleAndLoanService.isLoanEligible(
          this.loan
        );
        this.multiple = this.matches.length > 1;
      } else {
        // if no matches, looks at eligibility of first vehicle in list
        if (this.vehicles.length > 0) {
          // eslint-disable-next-line prefer-destructuring
          this.vehicle = this.vehicles[0];
          this.isVehicleIneligible = !VehicleAndLoanService.isVehicleEligible(
            this.vehicle
          );
          this.multiple = this.vehicles.length > 1;
        }
        // if no matches, looks at eligibility of first loan in list
        if (this.loans.length > 0) {
          // eslint-disable-next-line prefer-destructuring
          this.loan = this.loans[0];
          this.isLoanIneligible = !VehicleAndLoanService.isLoanEligible(
            this.loan
          );
          this.multiple = this.loans.length > 1;
        }
      }
    }
  }

  header() {
    let type = '';
    let plural = '';
    let contraction = "isn't";
    if (this.isVehicleIneligible) {
      type = 'car';
      if (
        this.matches.length > 1 ||
        (this.matches.length === 0 && this.vehicles.length > 1)
      ) {
        plural = 's';
        contraction = "aren't";
      }
    } else if (this.isLoanIneligible) {
      type = 'loan';
      if (
        this.matches.length > 1 ||
        (this.matches.length === 0 && this.loans.length > 1)
      ) {
        plural = 's';
        contraction = "aren't";
      }
    }
    return `The ${type}${plural} we found ${contraction} eligible for refinancing`;
  }

  subheader() {
    let issue = '';
    if (this.isVehicleIneligible) {
      issue = VehicleService.getWarningText(
        this.vehicle.ineligibleReasons[0],
        this.vehicle.maxVehicleAge
      );
    } else if (this.isLoanIneligible) {
      issue = `${LoanService.getWarningText(this.loan)}.`;
    }

    return html`<p class="fs-text">
      ${issue} Don't worry, this decision
      <span class="fs-text-weight--semibold"
        >did not impact your credit score.</span
      >
    </p>`;
  }

  card() {
    if (!this.fromVehicleSearch && this.matches.length > 0) {
      const mapper = (match, i) =>
        html`<refi-iconic-vehicle-loan-card
          .vehicle=${match.vehicle}
          .loan=${match.loan}
          .multiple=${this.matches.length > 1}
          vehicleAndLoanIndex=${i}
          .showButton=${false}
          hide-ineligibility
        ></refi-iconic-vehicle-loan-card>`;
      return this.matches.map(mapper);
    }
    if (this.isVehicleIneligible) {
      const mapper = (vehicle, i) =>
        html`<refi-iconic-vehicle-card
          .vehicle=${vehicle}
          .vehicleIndex=${i}
          hide-ineligibility
        ></refi-iconic-vehicle-card>`;
      return this.vehicles.map(mapper);
    }
    if (this.isLoanIneligible) {
      const mapper = (loan, i) =>
        html`<refi-iconic-loan-card
          .loan=${loan}
          loanIndex=${i}
          hide-ineligibility
        ></refi-iconic-loan-card>`;
      return this.loans.map(mapper);
    }
    return html``;
  }

  returnText() {
    let type = '';
    if (this.isLoanIneligible) {
      type = 'loan';
    }
    if (this.isVehicleIneligible || (this.vehicle && this.loan)) {
      type = 'car';
    }
    return `I want to refinance a different ${type}`;
  }

  exitPrequal() {
    document.location.href = 'https://www.capitalone.com/';
  }

  refinanceDifferentCarLoan() {
    const event = new CustomEvent('go-to-vehicle', {
      bubbles: true,
      composed: true,
      detail: true,
    });
    this.dispatchEvent(event);
  }

  sendHostAppHeaderUpdates() {
    this.dispatchEvent(
      new CustomEvent('show-progress-bar', {
        detail: {
          displayBar: true,
        },
        composed: true,
        bubbles: true,
      })
    );
    this.dispatchEvent(
      new CustomEvent('header-bottom-slot-color-change', {
        bubbles: true,
        composed: true,
        detail: { colored: false },
      })
    );
  }
}

customElements.define('refi-iconic-exit', RefiIconicExit);
